import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import ConfidentialityUnlock from './ConfidentialityUnlock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OPERATOR_TYPES = [
  { id: 'private_landlord', label: 'Private / portfolio landlord', userType: 'housing_professional' },
  { id: 'managing_agent', label: 'Managing / letting agent', userType: 'housing_professional' },
  { id: 'property_manager', label: 'Property / block / estate manager', userType: 'housing_professional' },
  { id: 'housing_association', label: 'Housing association', userType: 'housing_provider' },
  { id: 'council', label: 'Council / local authority', userType: 'housing_provider' },
  { id: 'supported_accommodation', label: 'Supported / temporary / managed accommodation', userType: 'housing_provider' },
  { id: 'rent_to_rent_hmo', label: 'Rent-to-rent / HMO operator', userType: 'housing_professional' },
  { id: 'rmc', label: 'Resident management company / co-operative', userType: 'resident_association' },
  { id: 'charity', label: 'Charity / community housing operator', userType: 'housing_provider' },
  { id: 'btr_pbsa', label: 'Build-to-rent / PBSA / institutional operator', userType: 'housing_professional' },
  { id: 'other', label: 'Other', userType: 'housing_professional' },
];

export const SCALE_BANDS = [
  '1–10 homes',
  '11–50 homes',
  '51–250 homes',
  '251–1,000 homes',
  '1,001–5,000 homes',
  '5,000+ homes',
  'Team/service only / not property-based',
  'Not sure',
];

const IMPROVE_FIRST = [
  'Repairs / issue reporting',
  'Resident communication',
  'Complaints / evidence',
  'Property / resident context',
  'Appointments / access',
  'Contractor coordination',
  'Notices / surveys',
  'Compliance evidence',
  'Resident onboarding / engagement',
  'Integration / interoperability',
  'Other',
];

const ProviderContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    operator_type: '',
    scale_band: '',
    improve_first: '',
    existing_systems: '',
    message: '',
    subscribe_blog: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (value) => setFormData((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.organisation || !formData.operator_type || !formData.scale_band) {
      setError('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    setError('');

    const operator = OPERATOR_TYPES.find((o) => o.id === formData.operator_type);
    const painParts = [];
    if (formData.improve_first) painParts.push(`Wants to improve first: ${formData.improve_first}`);
    if (formData.message) painParts.push(formData.message);

    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            user_type: operator?.userType || 'housing_professional',
            organisation: formData.organisation,
            operator_type: operator?.label || formData.operator_type,
            scale_band: formData.scale_band,
            existing_systems: formData.existing_systems,
            interested_in_pilot: true,
            pain_points: painParts.join('\n\n'),
            desired_features: '',
            subscribe_blog: formData.subscribe_blog,
            interest_type: 'provider_pilot',
            source_page: '/providers',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit');
        }

        setIsSuccess(true);
        setIsSubmitting(false);
        return;
      } catch (err) {
        if (attempt < maxRetries && err.message === 'Failed to fetch') {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        setError(err.message || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }
    }
  };

  if (isSuccess) {
    return (
      <div data-testid="provider-form-success" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-[#1F1E1E]">Thanks — your pilot registration is in</h3>
        <p className="text-[#3B3535]/70 max-w-md mx-auto">
          We&apos;ve received your registration and will review it to tailor the right pilot scope
          and touchpoints for you — then be in touch to get started.
        </p>
        <ConfidentialityUnlock email={formData.email} name={formData.name} source="provider-pilot-form" />
      </div>
    );
  }

  return (
    <div data-testid="provider-contact-form" className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="prov-name" className="text-[#3B3535] font-medium">
              Name <span className="text-[#B5541B]">*</span>
            </Label>
            <Input
              id="prov-name"
              data-testid="provider-name-input"
              value={formData.name}
              onChange={(e) => set('name')(e.target.value)}
              placeholder="Your name"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prov-email" className="text-[#3B3535] font-medium">
              Work email <span className="text-[#B5541B]">*</span>
            </Label>
            <Input
              id="prov-email"
              data-testid="provider-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => set('email')(e.target.value)}
              placeholder="you@organisation.com"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="prov-org" className="text-[#3B3535] font-medium">
            Organisation <span className="text-[#B5541B]">*</span>
          </Label>
          <Input
            id="prov-org"
            data-testid="provider-org-input"
            value={formData.organisation}
            onChange={(e) => set('organisation')(e.target.value)}
            placeholder="Organisation name"
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#3B3535] font-medium">
              Organisation / operator type <span className="text-[#B5541B]">*</span>
            </Label>
            <Select value={formData.operator_type} onValueChange={set('operator_type')}>
              <SelectTrigger data-testid="provider-operator-type-select" className="border-gray-200">
                <SelectValue placeholder="Select operator type" />
              </SelectTrigger>
              <SelectContent>
                {OPERATOR_TYPES.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#3B3535] font-medium">
              Approximate portfolio / service size <span className="text-[#B5541B]">*</span>
            </Label>
            <Select value={formData.scale_band} onValueChange={set('scale_band')}>
              <SelectTrigger data-testid="provider-scale-band-select" className="border-gray-200">
                <SelectValue placeholder="Select size band" />
              </SelectTrigger>
              <SelectContent>
                {SCALE_BANDS.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#3B3535] font-medium">
              What do you want to improve first? <span className="text-[#3B3535]/50">(optional)</span>
            </Label>
            <Select value={formData.improve_first} onValueChange={set('improve_first')}>
              <SelectTrigger data-testid="provider-improve-first-select" className="border-gray-200">
                <SelectValue placeholder="Select an area" />
              </SelectTrigger>
              <SelectContent>
                {IMPROVE_FIRST.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prov-systems" className="text-[#3B3535] font-medium">
              Existing systems / tools <span className="text-[#3B3535]/50">(optional)</span>
            </Label>
            <Input
              id="prov-systems"
              data-testid="provider-systems-input"
              value={formData.existing_systems}
              onChange={(e) => set('existing_systems')(e.target.value)}
              placeholder="e.g. spreadsheets, HMS, repairs platform"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="prov-message" className="text-[#3B3535] font-medium">
            Preferred pilot scope or anything else <span className="text-[#3B3535]/50">(optional)</span>
          </Label>
          <Textarea
            id="prov-message"
            data-testid="provider-message-input"
            value={formData.message}
            onChange={(e) => set('message')(e.target.value)}
            placeholder="e.g. one estate, one team, a handful of properties..."
            rows={3}
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none"
          />
        </div>

        {error && (
          <div data-testid="provider-form-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
        )}

        <div className="flex items-start gap-3 pt-1 pb-1 border-t border-gray-100 mt-1">
          <Checkbox
            id="prov-subscribe-blog"
            data-testid="provider-subscribe-blog"
            checked={formData.subscribe_blog}
            onCheckedChange={(v) => set('subscribe_blog')(!!v)}
            className="mt-0.5 data-[state=checked]:bg-[#B5541B] data-[state=checked]:border-[#B5541B]"
          />
          <Label htmlFor="prov-subscribe-blog" className="text-sm text-[#3B3535]/80 leading-relaxed cursor-pointer">
            Email me when new blog posts go live (occasional, no spam)
          </Label>
        </div>

        <Button
          data-testid="provider-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-8 h-auto py-5 rounded-full shadow-lg w-full"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Register for the provider pilot'
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProviderContactForm;
